import React from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { Box } from '@rbilabs/components-library/build/components/layout';
import { format, isTomorrow, isValid } from 'date-fns';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import ActionButton from 'components/action-button';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import useRoutes from 'hooks/use-routes';
import { useLocationContext } from 'state/location';
import { routes } from 'utils/routing';

const Content = styled(ModalContent)`
  > div {
    justify-content: space-between;
    padding-bottom: 2rem;
  }
  flex-direction: column;
`;

const Header = styled(ModalHeading)`
  margin-bottom: 10px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 280px;
  & strong {
    margin-top: 10px;
    font-weight: ${Styles.fontWeight.heavy};
    color: ${Styles.color.validated};
  }
  ${Styles.desktop`
    max-width: 500px;
  `}
`;

const IconWrapper = styled.div`
  margin: 2rem 0;
`;

const P = styled.p`
  text-align: center;
`;

interface ILocationState {
  nextEarliestOpen: Date;
}

const NoStoresOpenDeliveryModal = () => {
  const {
    location: { state },
  } = useLocationContext();
  const navigate = useNavigate();
  const { getLocalizedRouteForPath } = useRoutes();
  const { formatMessage } = useIntl();
  const { nextEarliestOpen } = state as ILocationState;
  const day = isTomorrow(nextEarliestOpen)
    ? formatMessage({ id: 'tomorrow' })
    : formatMessage({ id: 'today' });

  const routeToStoreLocator = () => navigate(getLocalizedRouteForPath('store-locator'));

  const modalHeading = formatMessage({ id: 'noStoresAreOpenForDelivery' });
  const modalBody = formatMessage({ id: 'closeMessageToFindNearbyStores' });

  return (
    <Modal
      onDismiss={routeToStoreLocator}
      mParticleEventData={{
        modalAppearanceEventMessage: modalHeading,
        modalMessage: modalBody,
      }}
    >
      <Content>
        <Container>
          <IconWrapper>
            <Icon icon="sadFace" color="primary" width="150px" height="150px" aria-hidden />
          </IconWrapper>

          <Header data-testid="heading" id="modal-heading">
            {modalHeading}
          </Header>

          {isValid(nextEarliestOpen) ? (
            // TODO: This should be one id that takes params
            <strong data-testid="availability">{`${formatMessage({
              id: 'available',
            })} ${day} ${formatMessage({
              id: 'at',
            })} ${format(nextEarliestOpen, 'hh:mm aa')}`}</strong>
          ) : null}

          <P data-testid="body">{modalBody}</P>
        </Container>

        <Box margin="0 auto">
          <ActionButton data-testid="dismissButton" onClick={() => navigate(routes.storeLocator)}>
            {formatMessage({ id: 'dismissDeliveryModal' })}
          </ActionButton>
        </Box>
      </Content>
    </Modal>
  );
};

export default NoStoresOpenDeliveryModal;
